import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Crown, MessageSquareText, TrendingDown } from 'lucide-react';

import { useWaypoint } from '../common/waypoint/WaypointProvider.jsx';
import ReviewListPopup from './ReviewListPopup';
import '../common/waypoint/waypoint.css';
import './feedback-list.css';

/* What each kind is called, and how its trainers are marked. The ratings
   each covers come from the API (`range`), so the wording can never
   disagree with what was counted. */
const KIND_COPY = {
  bad: {
    title: 'Bad Feedbacks',
    Icon: TrendingDown,
    noun: 'bad',
    sub: (r) => `The trainers students rate ${r.max} and below most — worst first.`,
    empty: (r) => `No bad feedbacks — no trainer has a rating of ${r.max} or below for these filters.`,
    avgTitle: 'Average of the bad ratings',
  },
  good: {
    title: 'Good Feedbacks',
    Icon: Crown,
    noun: 'good',
    sub: (r) => `The trainers students rate ${r.min} and above most — the top five are gold.`,
    empty: (r) => `No good feedbacks yet — no trainer has a rating of ${r.min} or above for these filters.`,
    avgTitle: 'Average of the good ratings',
  },
};

/* The button that opens every review behind one trainer. */
const ReviewsButton = ({ onClick, label }) => (
  <button type="button" className="pgm-btn brs-reviews-btn" onClick={onClick} aria-haspopup="dialog" aria-label={label}>
    <MessageSquareText aria-hidden="true" />
    Feedbacks
  </button>
);

/**
 * The bad -- or the good -- reviews, as a box of five trainers on
 * /session-feedback.
 *
 * `items` is the first page of review-lists grouped by trainer, ranked by
 * the API: for bad, the five trainers with the most bad reviews, each with
 * a falling arrow on a soft red card; for good, the five students rate best,
 * as gold cards with a crown. Five and no more; "View more" opens the popup
 * with every trainer and batch.
 *
 * Each trainer's Reviews button opens ReviewListPopup: every review for
 * them, searchable and paged, with the page's own filters. Their name opens
 * the bridge (waypoint) popup. Every figure is the API's; this only lays
 * them out.
 */
const ReviewsSection = ({
  kind = 'bad',
  items = [],
  total = 0,
  range,
  loading = false,
  baseParams,
  onView,
}) => {
  const { open: openEntity } = useWaypoint();
  const copy = KIND_COPY[kind] || KIND_COPY.bad;
  const { Icon } = copy;
  const band = range || (kind === 'good' ? { min: 4, max: 5 } : { min: 1, max: 3 });
  // { title, sub, filter } of the reviews popup, or null.
  const [reviewing, setReviewing] = useState(null);
  const shown = items.slice(0, 5);

  return (
    <section className={`brs brs--${kind}`} aria-label={copy.title}>
      <header className="brs-head">
        <div>
          <h5 className={`brs-title brs-title--${kind}`}>
            <Icon aria-hidden="true" />
            {copy.title}
          </h5>
          <p className="brs-sub">{copy.sub(band)}</p>
        </div>
        {total > 0 && <span className="brs-count">{total}</span>}
      </header>

      {loading && shown.length === 0 ? (
        <p className="brs-state">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="brs-state">{copy.empty(band)}</p>
      ) : (
        <>
          <ol className="brs-fav-list">
            {shown.map((t) => (
              <li className={`brs-fav brs-fav--${kind}`} key={t.trainerId || t.rank}>
                <span className="brs-fav-rank" aria-label={`Rank ${t.rank}`}>
                  <Icon aria-hidden="true" />
                  {t.rank}
                </span>
                <span className="brs-fav-id">
                  {t.trainerId ? (
                    <button
                      type="button"
                      className="wp-link brs-fav-name"
                      onClick={() => openEntity('trainer', t.trainerId)}
                    >
                      {t.trainerName || 'Unknown trainer'}
                    </button>
                  ) : (
                    <span className="brs-fav-name">{t.trainerName || 'Unknown trainer'}</span>
                  )}
                  <span className="brs-fav-meta">
                    {t.studentCount} {t.studentCount === 1 ? 'student' : 'students'} · {t.reviewCount}{' '}
                    {copy.noun} {t.reviewCount === 1 ? 'feedback' : 'feedbacks'}
                    {' · '}
                    {t.batchCount} {t.batchCount === 1 ? 'batch' : 'batches'}
                  </span>
                </span>
                <span className="brs-fav-avg" title={copy.avgTitle}>
                  {Number(t.averageReviewRating || 0).toFixed(1)}
                </span>
                {t.trainerId && (
                  <ReviewsButton
                    label={`Feedbacks for ${t.trainerName || 'this trainer'}`}
                    onClick={() =>
                      setReviewing({
                        title: t.trainerName || 'Trainer',
                        sub: 'Every feedback for this trainer',
                        filter: { trainerId: t.trainerId },
                      })
                    }
                  />
                )}
              </li>
            ))}
          </ol>

          <div className="brs-foot">
            {/* The way to everything behind the five: filled, in the theme
                colour, so it reads as the section's one action. */}
            <button type="button" className="pg-btn pg-btn--main" onClick={onView} aria-haspopup="dialog">
              View more
              {total > shown.length && ` (${total - shown.length} more)`}
            </button>
          </div>
        </>
      )}

      {reviewing && createPortal(
        <ReviewListPopup
          title={reviewing.title}
          sub={reviewing.sub}
          filter={reviewing.filter}
          baseParams={baseParams}
          initialBand={kind}
          onClose={() => setReviewing(null)}
        />,
        document.body,
      )}
    </section>
  );
};

export default ReviewsSection;
